import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Container, Text, Title, Paper, Button, Skeleton, Loader, Group, Tooltip } from '@mantine/core';
import { DateTime } from 'luxon';
import { IconCircleCheck, IconHelp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const API_BASE_URL = window.location.hostname === 'alt-service.lingva-pro.org'
  ? window.location.origin + '/api'
  : (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api');

// Разрешённые типы файлов и лимиты размера. Должны совпадать с серверной валидацией
// (Back/fileValidation.js). Сервер остаётся источником истины (проверяет ещё и сигнатуру).
const MB = 1024 * 1024;
const ALLOWED_FILE_TYPES = [
  { exts: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'], maxSize: 25 * MB },
  { exts: ['pdf'], maxSize: 50 * MB },
  { exts: ['txt'], maxSize: 10 * MB },
  { exts: ['doc', 'docx'], maxSize: 25 * MB },
  { exts: ['mp4', 'm4v', 'mov', 'webm'], maxSize: 124 * MB },
];
// Строка для атрибута accept (подсказка в диалоге выбора файла)
const ACCEPT_ATTR = ALLOWED_FILE_TYPES.flatMap(r => r.exts).map(e => `.${e}`).join(',');

const getFileExtension = (name) => (name.split('.').pop() || '').toLowerCase();
const findFileRule = (name) => ALLOWED_FILE_TYPES.find(r => r.exts.includes(getFileExtension(name)));

function Homework() {
  const { t, i18n } = useTranslation();
  const { userId, taskId } = useParams();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [textAnswer, setTextAnswer] = useState(() => localStorage.getItem('textAnswer') || '');
  const [taskData, setTaskData] = useState(null);
  const [timezone, setTimezone] = useState(() => localStorage.getItem('timezone') || 'UTC');
  const [randomImage, setRandomImage] = useState(null);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * 7);
    setRandomImage(`/Task_image/${randomIndex}.svg`);
  }, []);

  useEffect(() => {
    localStorage.setItem('textAnswer', textAnswer);
  }, [textAnswer]);

  useEffect(() => {
    const fetchTaskData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/task/${taskId}`);
        if (!response.ok) {
          throw new Error(`Ошибка HTTP: ${response.status}`);
        }
        const data = await response.json();
        setTaskData(data);

        if (data.ReadOnly) {
          return;
        }

        if (data.status === 'Not Started') {
          await fetch(`${API_BASE_URL}/statuspost/${data.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'Started' }),
          });
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных задачи:', error.message);
      }
    };

    fetchTaskData();
  }, [taskId]);

  useEffect(() => {
    try {
      const savedFiles = JSON.parse(localStorage.getItem('uploadedFiles')) || [];
      if (Array.isArray(savedFiles)) {
        setUploadedFiles((prev) => {
          if (JSON.stringify(prev) !== JSON.stringify(savedFiles)) {
            return savedFiles.map((file) => ({ ...file, status: 'uploaded', progress: 100 }));
          }
          return prev;
        });
      } else {
        localStorage.removeItem('uploadedFiles');
      }
    } catch (error) {
      console.error('Ошибка при восстановлении uploadedFiles из localStorage:', error);
      localStorage.removeItem('uploadedFiles');
    }
  }, []);

  useEffect(() => {
    try {
      const savedFiles = uploadedFiles
        .filter((file) => file.status === 'uploaded' && file.id)
        .map((file) => ({ id: file.id, name: file.name }));

      if (savedFiles.length > 0) {
        localStorage.setItem('uploadedFiles', JSON.stringify(savedFiles));
      }
    } catch (error) {
      console.error('Ошибка при сохранении uploadedFiles в localStorage:', error);
    }
  }, [uploadedFiles]);

  const formatDate = (utcDate, timezone, isDeadline = false) => {
    if (!utcDate) return null;
    const language = localStorage.getItem('language') || (navigator.language.startsWith('ru') ? 'Русский' : 'English');
    const isoDate = utcDate.replace(' ', 'T');
    const date = DateTime.fromISO(isoDate, { zone: 'utc' });

    if (date.toFormat('HH:mm:ss') === '04:00:00') {
      return date.setLocale(language === 'Русский' ? 'ru' : 'en').toFormat('d MMMM yyyy');
    }

    const localizedDate = date.setZone(timezone);
    if (!localizedDate.isValid) return t('invalidDate');

    const time = localizedDate.toFormat('HH:mm');
    if (isDeadline && time === '00:00') {
      return localizedDate.setLocale(language === 'Русский' ? 'ru' : 'en').toFormat('d MMMM yyyy');
    }
    return localizedDate.setLocale(language === 'Русский' ? 'ru' : 'en').toFormat('d MMMM yyyy, HH:mm');
  };

  const handleFileUpload = async (file, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append('file', file);

      xhr.open('POST', `${API_BASE_URL}/upload`, true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          resolve(response.attachmentsId);
        } else {
          reject(new Error(`Ошибка HTTP: ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Ошибка при загрузке файла'));
      };

      xhr.send(formData);
    });
  };

  const handleFileRemove = (fileName) => {
    setUploadedFiles((prev) => {
      const updatedFiles = prev.filter((file) => file.name !== fileName);
      const savedFiles = updatedFiles
        .filter((file) => file.status === 'uploaded' && file.id)
        .map((file) => ({ id: file.id, name: file.name }));
      localStorage.setItem('uploadedFiles', JSON.stringify(savedFiles));
      return updatedFiles;
    });
  };

  const handleFileSelection = async (files) => {
    const validFiles = Array.from(files).filter((file) => {
      const rule = findFileRule(file.name);
      if (!rule) {
        alert(t('fileTypeNotAllowed', { fileName: file.name }));
        return false;
      }
      if (file.size > rule.maxSize) {
        alert(t('fileSizeExceededType', { fileName: file.name, maxSize: Math.round(rule.maxSize / MB) }));
        return false;
      }
      return true;
    });

    for (const file of validFiles) {
      const xhr = new XMLHttpRequest();
      setUploadedFiles((prev) => [...prev, { name: file.name, id: null, status: 'uploading', progress: 0, xhr }]);

      try {
        const attachmentsId = await handleFileUpload(file, (progress) => {
          setUploadedFiles((prev) =>
            prev.map((f) => f.name === file.name ? { ...f, progress } : f)
          );
        });

        setUploadedFiles((prev) =>
          prev.map((f) => f.name === file.name ? { ...f, id: attachmentsId, status: 'uploaded', progress: 100, xhr: null } : f)
        );
      } catch (error) {
        console.error('Ошибка при загрузке файла:', error.message);
        alert(t('fileUploadError', { fileName: file.name }));
        setUploadedFiles((prev) =>
          prev.map((f) => f.name === file.name ? { ...f, status: 'error', progress: 0, xhr: null } : f)
        );
      }
    }
  };

  const handleSubmit = async () => {
    if (!textAnswer.trim()) {
      alert(t('enterTextToSubmit'));
      return;
    }

    if (!textAnswer.trim() && uploadedFiles.length === 0) {
      alert(t('enterAnswerOrUploadFile'));
      return;
    }

    try {
      const attachmentsIds = uploadedFiles
        .filter((file) => file.status === 'uploaded' && file.id)
        .map((file) => file.id);

      const response = await fetch(`${API_BASE_URL}/task/post/${taskData.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ post: textAnswer, attachmentsIds }),
      });

      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }

      await fetch(`${API_BASE_URL}/statuspost/${taskData.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'Assignment' }),
      });

      alert(t('answerSuccessfullySubmitted'));
      setTextAnswer('');
      setUploadedFiles([]);
      localStorage.removeItem('uploadedFiles');

      setTimeout(async () => {
        try {
          const updatedResponse = await fetch(`${API_BASE_URL}/task/${taskData.id}`);
          if (!updatedResponse.ok) {
            throw new Error(`Ошибка HTTP: ${updatedResponse.status}`);
          }
          const updatedData = await updatedResponse.json();
          setTaskData(updatedData);
        } catch (error) {
          console.error('Ошибка при обновлении данных задачи:', error.message);
        }
      }, 2000);
    } catch (error) {
      console.error('Ошибка при отправке ответа:', error.message);
      alert(t('errorSubmittingAnswer'));
    }
  };

  const renderStream = () => {
    if (!taskData?.stream) return null;

    return Object.values(taskData.stream)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .map((entry) => {
        const isUser = entry.isStudent === true; // классификацию считает бэкенд; id учителей сюда не приходят
        return (
          <motion.div
            key={entry.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Paper
              p="lg"
              radius="lg"
              style={{
                marginTop: 20,
                border: '1px solid rgba(0,0,0,0.06)',
                backgroundColor: isUser ? 'rgba(245, 158, 11, 0.04)' : 'rgba(14, 165, 233, 0.06)',
                maxWidth: 1000,
                minWidth: 200,
                width: 'fit-content',
                marginLeft: isUser ? 'auto' : 0,
                marginRight: isUser ? 0 : 'auto',
              }}
            >
              <Text
                size="xs"
                fw={500}
                c={isUser ? 'var(--text-secondary)' : 'var(--primary)'}
                ta={isUser ? 'right' : 'left'}
                style={{ opacity: 0.6 }}
                mb={4}
              >
                {isUser ? t('yourAnswer') : t('teacherMessage')}
              </Text>
              <Text size="sm" c="var(--text-secondary)">
                {entry.post && (
                  <span dangerouslySetInnerHTML={{ __html: entry.post }} />
                )}
              </Text>
              {entry.attachmentsIds?.length > 0 && (
                <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 10, marginTop: 20 }}>
                  <Group gap={5} mb="sm">
                    <Text size="sm" fw={300} c="var(--text-primary)">
                      {t('attachedFiles')}
                    </Text>
                    <Tooltip label={t('attachedFilesTooltip')} withArrow>
                      <IconHelp size={14} color="var(--text-muted)" style={{ opacity: 0.7, cursor: 'pointer' }} />
                    </Tooltip>
                  </Group>
                  {entry.attachmentsIds.map((attachmentId) => (
                    <Group key={attachmentId} gap={5} mb="sm">
                      <a href={`${API_BASE_URL}/download/${attachmentId}`} download style={{ display: 'inline-block' }}>
                        <img src="/file.svg" alt="File Icon" style={{ width: 24, height: 24 }} />
                      </a>
                      <Text
                        component="a"
                        href={`${API_BASE_URL}/download/${attachmentId}`}
                        download
                        size="sm"
                        c="var(--primary)"
                        style={{ textDecoration: 'none' }}
                        title={entry.attachmentsNames?.[attachmentId] || t('file')}
                      >
                        {(entry.attachmentsNames?.[attachmentId] || t('file')).length > 48
                          ? `${(entry.attachmentsNames?.[attachmentId] || t('file')).slice(0, 45)}...`
                          : entry.attachmentsNames?.[attachmentId] || t('file')}
                      </Text>
                    </Group>
                  ))}
                </div>
              )}
            </Paper>
          </motion.div>
        );
      });
  };

  return (
    <div style={{ backgroundColor: '#f0f9ff', minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <Container size="xl">
          <Group h={64} justify="space-between">
            <RouterLink to={`/${userId}`} style={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="LingvaPro Logo" style={{ height: 40 }} />
            </RouterLink>
            <Button
              variant="subtle"
              color="dark"
              component={RouterLink}
              to={`/${userId}`}
            >
              {t('returnToMain')}
            </Button>
          </Group>
        </Container>
      </header>

      <Container size="xl" mt="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Title order={2} fw={700} mb="lg">
            {taskData?.status === 'Completed' ? t('homeworkCompleted') : t('homework')}
          </Title>

          {/* Task Description Card */}
          <Paper p="lg" radius="xl" className="glass-card" style={{ cursor: 'default' }}>
            {taskData ? (
              <div>
                <Text size="sm" fs="italic" mb="sm" td={taskData?.status === 'Completed' ? 'line-through' : 'none'}>
                  {taskData.dateEnd ? `${t('completeBy')} ${formatDate(taskData.dateEnd, timezone, true)}` : t('noDeadline')}
                </Text>
                <Title order={4} fw={700} mb="sm">
                  {taskData.name}
                </Title>
                <Text size="md" mb="lg">
                  {taskData.description && (
                    <span dangerouslySetInnerHTML={{ __html: taskData.description }} />
                  )}
                </Text>
                <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', marginTop: 20 }} />
              </div>
            ) : (
              <div>
                <Skeleton height={30} width="60%" mb="sm" />
                <Skeleton height={40} width="80%" mb="sm" />
                <Skeleton height={100} width="100%" mb="lg" />
              </div>
            )}

            {taskData && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 20 }}>
                <div style={{ flex: 1 }}>
                  {taskData.links?.length > 0 && (
                    <>
                      <Title order={5} fw={300} mb="sm">{t('attachedLinks')}</Title>
                      {taskData.links.map((link, index) => (
                        <Group key={index} gap={5} mb="sm">
                          <img src="/link.svg" alt="Link Icon" style={{ width: 24, height: 24 }} />
                          <Text
                            component="a"
                            href={link.startsWith('http://') || link.startsWith('https://') ? link : `https://${link}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            c="var(--primary)"
                            style={{ textDecoration: 'none', wordBreak: 'break-word' }}
                            title={link}
                          >
                            {link.length > 48 ? `${link.slice(0, 45)}...` : link}
                          </Text>
                        </Group>
                      ))}
                    </>
                  )}
                  {taskData.attachments?.length > 0 && (
                    <>
                      <Group gap={5} mt="lg" mb="sm">
                        <Title order={5} fw={300}>{t('attachedFiles')}</Title>
                        <Tooltip label={t('attachedFilesTooltip')} withArrow>
                          <IconHelp size={16} color="var(--text-muted)" style={{ opacity: 0.7, cursor: 'pointer' }} />
                        </Tooltip>
                      </Group>
                      {taskData.attachments.map((attachment) => (
                        <Group key={attachment.id} gap={5} mb="sm">
                          <a href={`${API_BASE_URL}/download/${attachment.id}`} download style={{ display: 'inline-block' }}>
                            <img src="/file.svg" alt="File Icon" style={{ width: 24, height: 24 }} />
                          </a>
                          <Text
                            component="a"
                            href={`${API_BASE_URL}/download/${attachment.id}`}
                            download
                            size="sm"
                            c="var(--primary)"
                            style={{ textDecoration: 'none', wordBreak: 'break-word' }}
                            title={attachment.name}
                          >
                            {attachment.name.length > 48 ? `${attachment.name.slice(0, 45)}...` : attachment.name}
                          </Text>
                        </Group>
                      ))}
                    </>
                  )}
                </div>
                {(taskData.links?.length > 0 || taskData.attachments?.length > 0) && randomImage && (
                  <div className="task-image-container">
                    <img src={randomImage} alt="Task Icon" style={{ width: 150, height: 150 }} />
                  </div>
                )}
              </div>
            )}
          </Paper>

          {/* Message Stream */}
          {renderStream()}

          {/* Answer Form */}
          <Paper p="lg" radius="xl" className="glass-card" mt={80} style={{ cursor: 'default' }}>
            <div className="homework-form-inner">
              <div style={{ flex: 1 }}>
                <Title order={4} fw={700} mb="sm">{t('sendTextToTeacher')}</Title>
                <textarea
                  placeholder={t('enterYourAnswer')}
                  value={textAnswer}
                  onChange={(e) => setTextAnswer(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: 200,
                    height: 'auto',
                    border: '2px solid #e2e8f0',
                    borderRadius: 12,
                    padding: 12,
                    fontSize: 16,
                    fontFamily: '"DM Sans", sans-serif',
                    resize: 'none',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    transition: 'border-color 0.2s ease',
                    outline: 'none',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#0EA5E9'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                />
              </div>
              <div className="homework-upload-section">
                <Group gap={5} mb="sm">
                  <Title order={4} fw={700}>{t('uploadFiles')}</Title>
                  <Tooltip label={t('uploadFilesTooltip')} withArrow>
                    <IconHelp size={18} color="var(--text-muted)" style={{ opacity: 0.7, cursor: 'pointer' }} />
                  </Tooltip>
                </Group>
                <div
                  style={{
                    padding: 20,
                    borderRadius: 16,
                    border: '2px dashed rgba(14, 165, 233, 0.3)',
                    marginBottom: 20,
                    textAlign: 'center',
                    backgroundColor: 'rgba(14, 165, 233, 0.02)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleFileSelection(e.dataTransfer.files);
                  }}
                  onClick={(e) => {
                    if (e.target.tagName !== 'BUTTON') {
                      document.getElementById('file-upload').click();
                    }
                  }}
                >
                  <input
                    type="file"
                    multiple
                    accept={ACCEPT_ATTR}
                    id="file-upload"
                    onChange={(e) => handleFileSelection(e.target.files)}
                    style={{ display: 'none' }}
                  />
                  <Text size="sm" c="var(--text-muted)" mb="sm">
                    {t('dragFilesHere')}
                  </Text>
                  <div style={{ marginTop: 10, textAlign: 'left' }}>
                    {uploadedFiles.map((file, index) => (
                      <Group key={index} gap="sm" mb={5}>
                        {file.status === 'uploaded' && (
                          <IconCircleCheck size={16} color="var(--success)" />
                        )}
                        {file.status === 'error' && <img src="/error.svg" alt="Error" style={{ width: 16, height: 16 }} />}
                        {file.status === 'uploading' && (
                          <Loader size={16} color="var(--primary)" />
                        )}
                        <Text size="sm">{file.name}</Text>
                        <Button
                          variant="subtle"
                          color="gray"
                          size="compact-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (file.status === 'uploading' && file.xhr) {
                              file.xhr.abort();
                            }
                            handleFileRemove(file.name);
                          }}
                          style={{ opacity: 0.6 }}
                        >
                          ✕
                        </Button>
                      </Group>
                    ))}
                  </div>
                </div>
                <Button
                  className="gradient-btn"
                  size="lg"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={uploadedFiles.some((file) => file.status === 'uploading') || taskData?.ReadOnly}
                >
                  {uploadedFiles.some((file) => file.status === 'uploading')
                    ? t('pleaseWaitForUpload')
                    : taskData?.ReadOnly
                    ? t('readOnlyTaskMessage')
                    : t('submitForReview')}
                </Button>
              </div>
            </div>
          </Paper>
        </motion.div>
      </Container>

      {/* Footer */}
      <div style={{ height: 40 }} />
      <Text ta="center" size="xs" c="var(--primary)" mb="sm">
        <RouterLink
          to={`/${userId}/guide`}
          style={{ textDecoration: 'none', color: 'var(--primary)' }}
        >
          {t('platformInstructions')}
        </RouterLink>
        {' | '}
        <a
          href={`https://${window.location.hostname === 'alt-service.lingva-pro.org' ? 'alt.lingva-pro.org' : 'class.lingva-pro.org'}/quality/${userId}?lg=${i18n.language}`}
          style={{ textDecoration: 'none', color: 'var(--primary)' }}
        >
          {t('videoConnectionTest')}
        </a>
      </Text>

      <style>
        {`
          .homework-form-inner {
            display: flex;
            flex-direction: column;
            gap: 20px;
          }
          @media (min-width: 768px) {
            .homework-form-inner {
              flex-direction: row;
            }
          }
          .homework-upload-section {
            width: 100%;
            margin-top: 20px;
          }
          @media (min-width: 768px) {
            .homework-upload-section {
              flex-shrink: 0;
              width: 300px;
              margin-top: 0;
            }
          }
          .task-image-container {
            flex-shrink: 0;
            margin-left: 20px;
            text-align: center;
            display: none;
            align-items: center;
          }
          @media (min-width: 768px) {
            .task-image-container {
              display: flex;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Homework;
